import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { 
  CheckCircle, ArrowRight, Zap, FileText, Bell, BarChart3, 
  Calculator, Clock, Shield, Users, Laptop, Coffee, Code, Palette
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';

const SolutionFreelance = () => {
  const features = [
    {
      icon: FileText,
      title: "Factures Factur-X conformes",
      description: "Générez automatiquement des factures au format Factur-X, obligatoire dès 2026."
    },
    {
      icon: Bell,
      title: "Relances automatiques",
      description: "Ne courez plus après vos paiements. Les relances s'envoient automatiquement."
    },
    {
      icon: Calculator,
      title: "Module URSSAF intégré",
      description: "Calculez vos cotisations en temps réel et anticipez vos charges trimestrielles."
    },
    {
      icon: BarChart3,
      title: "Dashboard freelance",
      description: "Visualisez votre CA, vos factures en attente et votre trésorerie en un coup d'œil."
    },
    {
      icon: Clock,
      title: "Gain de temps",
      description: "Créez une facture en 30 secondes au lieu de 10 minutes. Plus de copier-coller."
    },
    {
      icon: Shield,
      title: "Stockage sécurisé",
      description: "Vos factures sont stockées et exportables pour votre archivage légal."
    }
  ];

  const useCases = [
    { icon: Code, label: "Développeurs" },
    { icon: Palette, label: "Graphistes" },
    { icon: Users, label: "Consultants" },
    { icon: Laptop, label: "Rédacteurs" },
    { icon: Coffee, label: "Formateurs" }
  ];

  const faqs = [
    {
      q: "SmartFacture AI est-il adapté aux freelances ?",
      a: "Absolument ! SmartFacture AI a été conçu spécifiquement pour les freelances et indépendants français. Mentions légales, TVA, URSSAF : tout est automatisé."
    },
    {
      q: "Comment fonctionne le module URSSAF ?",
      a: "Le module calcule automatiquement vos cotisations en fonction de votre CA et de votre statut (micro-entrepreneur, EI, EURL...). Vous voyez en temps réel ce que vous devrez payer."
    },
    {
      q: "Puis-je importer mes anciens clients et factures ?",
      a: "Oui, vous pouvez importer vos données existantes via CSV ou saisie manuelle. Notre équipe vous accompagne gratuitement."
    },
    {
      q: "Les factures sont-elles conformes à la réforme 2026 ?",
      a: "Oui, toutes les factures sont générées au format Factur-X (PDF/A-3 + XML), le standard obligatoire à partir de septembre 2026."
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": { "@type": "Answer", "text": faq.a }
    }))
  };

  return (
    <>
      <Helmet>
        <title>Logiciel Facturation Freelance | SmartFacture AI - Factures Factur-X</title>
        <meta name="description" content="Logiciel de facturation pour freelances. Factures Factur-X conformes 2026, relances automatiques, module URSSAF intégré. Essai gratuit 7 jours." />
        <link rel="canonical" href="https://smartfacture-ai.com/solutions/freelance" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-slate-50">
        {/* Header */}
        <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-xl flex items-center justify-center">
                <Zap className="h-5 w-5 text-white" />
              </div>
              <span className="font-bold text-xl text-slate-900">SmartFacture AI</span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/outils/facture-gratuite" className="text-slate-600 hover:text-slate-900 hidden sm:block">
                Outil gratuit
              </Link>
              <Link to="/register">
                <Button className="bg-blue-600 hover:bg-blue-700">Essai gratuit</Button>
              </Link>
            </div>
          </div>
        </header>

        {/* Hero */}
        <section className="bg-gradient-to-br from-violet-600 to-purple-700 text-white py-16 lg:py-24">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center max-w-3xl mx-auto">
              <Badge className="bg-white/20 text-white border-white/30 mb-6">
                Solution Freelance
              </Badge>
              <h1 className="text-4xl lg:text-5xl font-bold mb-6">
                Le logiciel de facturation pensé pour les freelances
              </h1>
              <p className="text-xl text-violet-100 mb-8">
                Créez des factures conformes en 30 secondes, automatisez vos relances et anticipez vos charges URSSAF. Tout ça, sans prise de tête.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/register">
                  <Button size="lg" className="w-full sm:w-auto bg-white text-violet-600 hover:bg-violet-50">
                    Essai gratuit 7 jours
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/demo">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto border-white text-white hover:bg-white/10">
                    Voir la démo
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Use Cases */}
        <section className="py-8 bg-white border-b">
          <div className="max-w-6xl mx-auto px-4">
            <div className="flex flex-wrap justify-center gap-6">
              {useCases.map((uc, i) => {
                const Icon = uc.icon;
                return (
                  <div key={i} className="flex items-center gap-2 text-slate-600">
                    <Icon className="h-5 w-5 text-violet-500" />
                    <span>{uc.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-16">
          <div className="max-w-6xl mx-auto px-4">
            <h2 className="text-3xl font-bold text-slate-900 text-center mb-4">
              Tout ce dont un freelance a besoin
            </h2>
            <p className="text-slate-600 text-center mb-12 max-w-2xl mx-auto">
              SmartFacture AI automatise votre facturation pour que vous puissiez vous concentrer sur votre activité.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((f, i) => {
                const Icon = f.icon;
                return (
                  <div key={i} className="bg-white rounded-xl p-6 shadow-sm border border-slate-200 hover:shadow-md transition-shadow">
                    <div className="w-12 h-12 bg-violet-100 rounded-xl flex items-center justify-center mb-4">
                      <Icon className="h-6 w-6 text-violet-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">{f.title}</h3>
                    <p className="text-slate-600">{f.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Pricing CTA */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4">
            <div className="bg-gradient-to-r from-violet-600 to-purple-600 rounded-2xl p-8 lg:p-12 text-white text-center">
              <h2 className="text-2xl lg:text-3xl font-bold mb-4">
                Commencez gratuitement
              </h2>
              <p className="text-violet-100 mb-6">
                À partir de 14€/mois. 7 jours d'essai gratuit sans carte bancaire.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/register">
                  <Button size="lg" className="bg-white text-violet-600 hover:bg-violet-50">
                    Démarrer l'essai gratuit
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/pricing">
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                    Voir les tarifs
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 bg-slate-100">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-3xl font-bold text-slate-900 text-center mb-12">
              Questions fréquentes
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {faqs.map((faq, i) => (
                <div key={i} className="bg-white rounded-xl p-6 shadow-sm">
                  <h3 className="font-semibold text-slate-900 mb-2">{faq.q}</h3>
                  <p className="text-slate-600 text-sm">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-slate-900 text-white py-8">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <p className="text-slate-400">© 2026 SmartFacture AI. Tous droits réservés.</p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default SolutionFreelance;
